// The data dictionary's vocabularies and the mapping between a DataColumn's (ColumnOut) stored
// fields and the editors' state: types, sensitivities, file roles, rules.

// Frictionless Table Schema types (backend unio.models.column.FIELD_TYPES).
export const FIELD_TYPES = ["string", "integer", "number", "boolean", "date", "datetime", "time",
  "duration", "object", "array", "geojson", "geopoint", "any"];

// File references (backend unio/validation/references.py): the logical type and the roles of
// _unio_object. raw = another file of a primary dataset, attachment = of a derived dataset
// (output data), protocol = a protocol file (an .eln, or a file marked so on the Files tab),
// table = the source file of a built table.
// Personal data (column sensitivity); the Select's empty option means "not personal data".
// "checked" (O19): looked at and NOT personal; nothing is masked and the validator stops asking.
export const SENSITIVITIES = [
  { value: "personal", label: "personal — identifies a person (name, e-mail)" },
  { value: "sensitive", label: "sensitive — special category (e.g. health)" },
  { value: "checked", label: "checked — not personal data (the check stops asking)" },
];
export const MARKED = ["personal", "sensitive"];

export const FILE_ROLES = [
  { value: "raw", label: "raw (primary data)" },
  { value: "attachment", label: "attachment (output data)" },
  { value: "protocol", label: "protocol" },
  { value: "table", label: "table" },
];

/** "fastq.gz, .BAM" -> [".fastq.gz", ".bam"] */
export function parseExtensions(text) {
  const out = [];
  parseMissing(text).forEach(s => {
    const e = (s.startsWith(".") ? s : `.${s}`).toLowerCase();
    if (!out.includes(e)) out.push(e);
  });
  return out;
}

/** "NA, -9999 ,," -> ["NA", "-9999"] */
export function parseMissing(text) {
  const out = [];
  (text || "").split(",").map(s => s.trim()).forEach(s => { if (s && !out.includes(s)) out.push(s); });
  return out;
}

// Dictionary rules (backend unio/validation/constraints.py): which rule fits which type. The
// data check and the built database (CHECK constraints) enforce them; the backend refuses a
// rule that does not fit (422).
export const RANGE_TYPES = ["integer", "number", "date", "datetime"];
export const TEXT_TYPES = ["string", "any"];
export const ENUM_TYPES = ["string", "any", "integer", "number", "date", "datetime", "boolean"];

const NUMERIC = ["integer", "number"];

function typedValue(text, type) {
  const s = String(text).trim();
  if (NUMERIC.includes(type) && s !== "" && !Number.isNaN(Number(s))) return Number(s);
  return s;
}

const filled = v => String(v ?? "").trim() !== "";

/** The editor's rule fields -> the `constraints` object for PATCH /api/columns/{id}. Rules that
 *  do not fit `type` are left out; empty fields mean "no rule". */
export function rulesPatch(r, type) {
  const out = {};
  if (r.required) out.required = true;
  if (r.unique) out.unique = true;
  if (RANGE_TYPES.includes(type)) {
    if (filled(r.minimum)) out.minimum = typedValue(r.minimum, type);
    if (filled(r.maximum)) out.maximum = typedValue(r.maximum, type);
  }
  if (TEXT_TYPES.includes(type)) {
    if (filled(r.minLength)) out.minLength = Number(r.minLength);
    if (filled(r.maxLength)) out.maxLength = Number(r.maxLength);
    if (filled(r.pattern)) out.pattern = r.pattern.trim();
  }
  if (ENUM_TYPES.includes(type)) {
    const values = parseMissing(r.enum).map(v => typedValue(v, type));
    if (values.length) out.enum = values;
  }
  return out;
}

/** A column's stored constraints -> the editor's rule fields. */
export function rulesState(cons) {
  const c = cons || {};
  const text = v => (v === undefined || v === null ? "" : String(v));
  return {
    required: !!c.required, unique: !!c.unique, minimum: text(c.minimum), maximum: text(c.maximum),
    minLength: text(c.minLength), maxLength: text(c.maxLength), pattern: c.pattern || "",
    enum: (c.enum || []).join(", "),
  };
}

/** Short labels of a column's rules beyond required/unique, for the dictionary table. */
export function ruleBadges(cons) {
  const c = cons || {};
  const out = [];
  if (c.minimum !== undefined && c.maximum !== undefined) out.push(`${c.minimum} … ${c.maximum}`);
  else if (c.minimum !== undefined) out.push(`≥ ${c.minimum}`);
  else if (c.maximum !== undefined) out.push(`≤ ${c.maximum}`);
  if (c.minLength !== undefined || c.maxLength !== undefined) {
    out.push(`length ${c.minLength ?? 0} … ${c.maxLength ?? "∞"}`);
  }
  if (c.pattern) out.push("pattern");
  if (c.enum) out.push(`${c.enum.length} allowed value${c.enum.length === 1 ? "" : "s"}`);
  return out;
}
